import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import GiftSendModal from "@/components/GiftSendModal";
import { useConfirm } from "@/components/ConfirmDialog";

export default function ViewObituary() {
  const { id } = useParams();
  const nav = useNavigate();
  const { confirm } = useConfirm();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // 'share' | 'oos' | 'ack' | 'quotes'
  const photoInputRef = useRef(null);

  // AI results
  const [quotes, setQuotes] = useState([]);
  const [quotesBusy, setQuotesBusy] = useState(false);
  const [oosForm, setOosForm] = useState({ venue: "", officiant: "", hymns: "", notes: "", scripture: true, eulogy: true, communion: false });
  const [oosResult, setOosResult] = useState("");
  const [oosBusy, setOosBusy] = useState(false);
  const [ackForm, setAckForm] = useState({ signed_by: "", special: "" });
  const [ackResult, setAckResult] = useState("");
  const [ackBusy, setAckBusy] = useState(false);
  const [shareEmail, setShareEmail] = useState("");
  const [shareBusy, setShareBusy] = useState(false);
  const [photosEmail, setPhotosEmail] = useState("");
  const [photosNote, setPhotosNote] = useState("");
  const [photosBusy, setPhotosBusy] = useState(false);
  const [editBody, setEditBody] = useState("");
  const [editName, setEditName] = useState("");
  const [editBusy, setEditBusy] = useState(false);
  const [presence, setPresence] = useState([]);
  const [giftOpen, setGiftOpen] = useState(false);
  const [publishBusy, setPublishBusy] = useState(false);
  const [videos, setVideos] = useState([]);
  const [candles, setCandles] = useState([]);
  const [guestbook, setGuestbook] = useState([]);
  // Cover customizer state
  const [coverForm, setCoverForm] = useState({ theme: "classic", cover_heading: "", badge_medallion: false });
  const [coverBusy, setCoverBusy] = useState(false);
  // Slideshow modal state
  const [slideForm, setSlideForm] = useState({
    tracks: [], voices: [],
    music_track: "",
    recipient_email: "",
    voiceover_mode: "none",
    voiceover_text: "",
    voiceover_voice: "nova",
    voiceover_b64: null,
    voiceover_filename: "",
  });
  const [slideBusy, setSlideBusy] = useState(false);
  // Rhyming poem checkout modal state
  const [poemForm, setPoemForm] = useState({ tone: "loving", notes: "" });
  const [poemBusy, setPoemBusy] = useState(false);
  // Printed program order modal state
  const [printForm, setPrintForm] = useState({ quantity: 100, name: "", addr1: "", city: "", state: "", zip: "" });
  const [printBusy, setPrintBusy] = useState(false);
  // Donations modal state
  const [donAccount, setDonAccount] = useState(null);
  const [donForm, setDonForm] = useState({ fund_name: "", email: "", goal_usd: 0, cause: "" });
  const [donBusy, setDonBusy] = useState(false);

  // Presence heartbeat — pings every 30s so team members see each other on the same obit.
  useEffect(() => {
    if (!id) return;
    const tick = async () => {
      try {
        await api.post("/presence/heartbeat", { obituary_id: id, action: "viewing" });
        const { data } = await api.get(`/presence/${id}`);
        setPresence(data.items || []);
      } catch { /* ignore */ }
    };
    tick();
    const t = setInterval(tick, 30000);
    return () => clearInterval(t);
  }, [id]);

  const refresh = async () => {
    const { data } = await api.get(`/obituaries/${id}`);
    setDoc(data);
  };

  useEffect(() => {
    api.get(`/obituaries/${id}`).then(({ data }) => setDoc(data)).catch(() => {
      toast.error("Could not load");
      nav("/dashboard");
    }).finally(() => setLoading(false));
  }, [id, nav]);

  // Detect Stripe Connect onboarding return — celebrate success or guide them back.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const c = params.get("connect");
    if (c === "success") {
      toast.success("Stripe Connect setup complete. Donations are now active on this memorial.", { duration: 8000 });
      window.history.replaceState({}, "", window.location.pathname);
      refresh();
    } else if (c === "refresh") {
      toast.message("Stripe needs a bit more info to finish setup. Click 'Manage donations' to re-open onboarding.", { duration: 8000 });
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  // Load tributes received + my video jobs (poll every 25s so previews-in-progress refresh)
  useEffect(() => {
    if (!id) return;
    const fetchAll = async () => {
      try {
        const [v, c, g] = await Promise.all([
          api.get(`/tribute-video/by-obituary/${id}`).catch(() => ({ data: { items: [] } })),
          api.get(`/memorial/${id}/candles`).catch(() => ({ data: { items: [] } })),
          api.get(`/memorial/${id}/guestbook`).catch(() => ({ data: { items: [] } })),
        ]);
        setVideos(v.data.items || []);
        setCandles(c.data.items || []);
        setGuestbook(g.data.items || []);
      } catch { /* ignore */ }
    };
    fetchAll();
    const t = setInterval(fetchAll, 25000);
    return () => clearInterval(t);
  }, [id]);

  // Live sync — poll every 20s so co-editors see each other's updates without a refresh.
  useEffect(() => {
    if (!id || !doc?.updated_at) return;
    const tick = async () => {
      try {
        const { data } = await api.get(`/obituaries/${id}`);
        if (data?.updated_at && data.updated_at !== doc.updated_at) {
          setDoc(data);
          toast.message("Refreshed — a co-editor made changes.");
        }
      } catch { /* ignore */ }
    };
    const t = setInterval(tick, 20000);
    return () => clearInterval(t);
  }, [id, doc?.updated_at]);

  if (loading) return <div style={{ padding: 80, textAlign: "center", color: "var(--text-muted)" }}>Loading…</div>;
  if (!doc) return null;

  // ─── Actions ─── //
  const copyText = async () => {
    await navigator.clipboard.writeText(doc.body || "");
    toast.success("Copied to clipboard.");
  };

  const downloadText = () => {
    const blob = new Blob([doc.body || ""], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `obituary-${doc.deceased_name.replace(/\s+/g, "-")}.txt`; a.click();
    URL.revokeObjectURL(url);
    toast.success("Saved.");
  };

  const downloadPdf = async () => {
    try {
      const token = localStorage.getItem("or_token");
      // iOS Safari blocks blob: URLs opened in new tabs ("WebKitBlobResource error 1").
      // Solution: navigate the CURRENT window to a same-origin PDF URL that carries the
      // JWT as a query param. Safari renders the PDF inline with native share/save.
      const url = `${API}/obituaries/${id}/pdf?token=${encodeURIComponent(token || "")}`;
      // Use an anchor click in the same user-gesture stack — most reliable across browsers.
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("PDF opening…");
      setModal(null);
    } catch (err) {
      toast.error("Could not download PDF.");
    }
  };

  const downloadBlob = async (url, body, filename) => {
    try {
      const res = await api.post(url, body, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = blobUrl; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(blobUrl);
      toast.success("Downloaded.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed.");
    }
  };

  const downloadPrayerCards = () => downloadBlob("/cards/prayer-card", {
    deceased_name: doc.deceased_name, born: doc.born, died: doc.died,
    portrait_b64: doc.portrait_b64, theme: doc.theme || "classic",
  }, `prayer-cards-${(doc.deceased_name || 'memorial').replace(/\s+/g, '-')}.pdf`);

  const downloadThankYouCards = () => {
    const family = prompt("Family name for the thank-you cards:", `The Family of ${doc.deceased_name}`);
    if (!family) return;
    downloadBlob("/cards/thank-you-card", {
      family_name: family, deceased_name: doc.deceased_name, theme: doc.theme || "classic",
    }, `thank-you-cards-${family.replace(/\s+/g, '-').toLowerCase()}.pdf`);
  };

  const startTributeVideo = async () => {
    let tracks = [];
    let voices = [];
    try {
      const { data } = await api.get("/tribute-video/music-library");
      tracks = data.tracks || [];
      voices = data.voices || [];
    } catch { /* ignore — proceed with no music */ }
    setSlideForm({
      tracks,
      voices,
      music_track: tracks[0]?.id || "peaceful_reflection.mp3",
      recipient_email: "",
      voiceover_mode: "none",   // "none" | "tts" | "upload"
      voiceover_text: "",
      voiceover_voice: voices[0]?.id || "nova",
      voiceover_b64: null,
      voiceover_filename: "",
    });
    setModal("slideshow");
  };

  const submitSlideshow = async (e) => {
    e.preventDefault();
    setSlideBusy(true);
    try {
      const body = {
        obituary_id: id,
        deceased_name: doc.deceased_name,
        music_track: slideForm.music_track,
        recipient_email: slideForm.recipient_email || null,
      };
      // Attach voiceover if user selected one
      if (slideForm.voiceover_mode === "tts" && slideForm.voiceover_text?.trim()) {
        body.voiceover_text = slideForm.voiceover_text.trim().slice(0, 4000);
        body.voiceover_voice = slideForm.voiceover_voice || "nova";
      } else if (slideForm.voiceover_mode === "upload" && slideForm.voiceover_b64) {
        body.voiceover_b64 = slideForm.voiceover_b64;
      }
      const { data } = await api.post("/tribute-video/generate-preview", body);
      if (data?.job_id) {
        toast.success("Slideshow is rendering — usually ready in 30–90 seconds.");
        setModal(null);
        window.location.href = `/tribute-video?job_id=${data.job_id}`;
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start slideshow.");
    } finally {
      setSlideBusy(false);
    }
  };

  const startRhymingPoem = () => {
    setPoemForm({ tone: "loving", notes: "" });
    setModal("poem-options");
  };

  const submitPoemCheckout = async () => {
    setPoemBusy(true);
    try {
      const { data } = await api.post("/poem/checkout", {
        obituary_id: id,
        deceased_name: doc.deceased_name,
        tone: poemForm.tone,
        scheme: "ABAB",
        extra_notes: poemForm.notes || "",
        origin_url: window.location.origin,
      });
      if (data.used_bundle_credit && data.redirect_url) {
        toast.success("Bundle credit applied — your poem is being composed!");
        window.location.href = data.redirect_url;
      } else if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start poem checkout.");
    } finally {
      setPoemBusy(false);
    }
  };

  const orderPrintedPrograms = () => {
    setPrintForm({ quantity: 100, name: "", addr1: "", city: "", state: "", zip: "" });
    setModal("print");
  };

  const submitPrintOrder = async (e) => {
    e.preventDefault();
    const q = parseInt(printForm.quantity, 10);
    if (q < 25 || q > 500) return toast.error("Quantity must be between 25 and 500.");
    setPrintBusy(true);
    try {
      const { data } = await api.post("/print/program", {
        obituary_id: id, quantity: q,
        shipping_name: printForm.name, shipping_address_line1: printForm.addr1,
        shipping_city: printForm.city, shipping_state: printForm.state, shipping_zip: printForm.zip,
      });
      toast.success(data.message || "Order recorded.");
      setModal(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Order failed.");
    } finally {
      setPrintBusy(false);
    }
  };

  const sendEmail = async (e) => {
    e.preventDefault();
    if (!shareEmail) return;
    setShareBusy(true);
    try {
      const { data } = await api.post("/obituaries/share", { obituary_id: id, recipient_email: shareEmail });
      if (data.sent) toast.success("Sent.");
      else toast.error("Email could not be sent.");
      setShareEmail("");
      setModal(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed.");
    } finally {
      setShareBusy(false);
    }
  };

  const sendPhotosEmail = async (e) => {
    e.preventDefault();
    if (!photosEmail) return;
    setPhotosBusy(true);
    try {
      const { data } = await api.post("/obituaries/photos/email", { obituary_id: id, recipient_email: photosEmail, note: photosNote });
      if (data.sent) toast.success(`Photos sent (${data.count}).`);
      else toast.error("Could not send photos.");
      setPhotosEmail(""); setPhotosNote("");
      setModal(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed.");
    } finally {
      setPhotosBusy(false);
    }
  };

  const openEdit = () => {
    setEditName(doc.deceased_name || "");
    setEditBody(doc.body || "");
    setModal("edit");
  };

  // Donations
  const openDonations = async () => {
    setDonForm({
      fund_name: doc.donation_fund_name || `In memory of ${doc.deceased_name}`,
      email: "",
      goal_usd: doc.donation_goal_usd || 0,
      cause: doc.donation_cause || "",
    });
    try {
      const { data } = await api.get(`/donations/account/${id}`);
      setDonAccount(data);
    } catch {
      setDonAccount({ enabled: false, onboarding_complete: false });
    }
    setModal("donations");
  };

  const startConnectOnboarding = async (e) => {
    e.preventDefault();
    if (!donForm.email) return toast.error("Enter the email for the family Stripe account.");
    if (!donForm.fund_name) return toast.error("Name the memorial fund.");
    setDonBusy(true);
    try {
      const { data } = await api.post("/donations/connect/onboard", {
        obituary_id: id,
        return_url: `${window.location.origin}/obituary/${id}?connect=success`,
        refresh_url: `${window.location.origin}/obituary/${id}?connect=refresh`,
        email: donForm.email,
        fund_name: donForm.fund_name,
      });
      if (data.onboarding_url) {
        toast.success("Redirecting to Stripe for onboarding…");
        window.location.href = data.onboarding_url;
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start onboarding.");
    } finally {
      setDonBusy(false);
    }
  };

  const saveDonationGoal = async (e) => {
    e.preventDefault();
    setDonBusy(true);
    try {
      await api.post("/donations/goal", {
        obituary_id: id,
        goal_usd: parseFloat(donForm.goal_usd) || 0,
        cause: donForm.cause,
      });
      toast.success("Donation goal saved. Visitors will see the progress bar.");
      await refresh();
      setModal(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setDonBusy(false);
    }
  };

  const openCover = () => {
    setCoverForm({
      theme: doc.theme || "classic",
      cover_heading: doc.cover_heading || "In Loving Memory",
      badge_medallion: !!doc.badge_medallion,
    });
    setModal("cover");
  };

  const saveCover = async (e) => {
    e.preventDefault();
    setCoverBusy(true);
    try {
      await persist({
        theme: coverForm.theme,
        cover_heading: coverForm.cover_heading,
        badge_medallion: coverForm.badge_medallion,
      });
      toast.success("Cover updated. Your next PDF will use these settings.");
      setModal(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save cover.");
    } finally {
      setCoverBusy(false);
    }
  };

  const togglePublish = async () => {
    setPublishBusy(true);
    try {
      const turnOn = !doc.public_enabled;
      if (turnOn) {
        const ok = await confirm({
          title: "Publish a public memorial page?",
          message: "Visitors will be able to light virtual candles (free), send digital sympathy cards ($3.99) and flower bouquets ($5.99), and sign the public guestbook. You can unpublish at any time.",
          confirmLabel: "Yes, publish",
          cancelLabel: "Not yet",
        });
        if (!ok) { setPublishBusy(false); return; }
      }
      await persist({ public_enabled: turnOn });
      toast.success(turnOn ? "Memorial published! Share the link to receive tributes." : "Memorial unpublished.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update publish status.");
    } finally {
      setPublishBusy(false);
    }
  };

  const copyMemorialLink = async () => {
    if (!doc?.public_slug) return;
    const url = `${window.location.origin}/m/${doc.public_slug}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Memorial link copied to clipboard.");
    } catch {
      // Clipboard blocked — show the URL inline in a toast for manual copy
      toast.message("Copy this link manually", { description: url, duration: 12000 });
    }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setEditBusy(true);
    try {
      await persist({ deceased_name: editName, body: editBody });
      toast.success("Obituary updated.");
      setModal(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save.");
    } finally {
      setEditBusy(false);
    }
  };

  const openMailto = () => {
    const subject = encodeURIComponent(`In loving memory of ${doc.deceased_name}`);
    // mailto: URLs have a ~2000-character limit on most platforms — truncate to be safe.
    const full = `${doc.body || ""}\n\n— Composed with Obituary Relief —`;
    const safeBody = full.length > 1400 ? full.slice(0, 1400) + "…\n\n(Full obituary continues — paste from clipboard or use the PDF.)" : full;
    const body = encodeURIComponent(safeBody);
    const href = `mailto:?subject=${subject}&body=${body}`;
    try {
      // Use an actual anchor click so iOS Safari treats it as a user gesture properly.
      const a = document.createElement("a");
      a.href = href;
      a.rel = "noopener noreferrer";
      document.body.appendChild(a); a.click(); a.remove();
      // Also copy full text to clipboard so user can paste the complete version if truncated.
      if (full.length > 1400 && navigator.clipboard) {
        navigator.clipboard.writeText(full).catch(() => {});
        toast.success("Mail app opening. Full text also copied to clipboard.");
      } else {
        toast.success("Opening your mail app…");
      }
      setModal(null);
    } catch {
      toast.error("Could not open mail app.");
    }
  };

  const downloadImage = (b64, name) => {
    try {
      const mime = name.toLowerCase().endsWith(".png") ? "image/png" : "image/jpeg";
      const dataUrl = `data:${mime};base64,${b64}`;
      fetch(dataUrl).then(r => r.blob()).then(blob => {
        const url = URL.createObjectURL(blob);
        const newWin = window.open(url, "_blank");
        if (!newWin) {
          const a = document.createElement("a");
          a.href = url; a.download = name;
          document.body.appendChild(a); a.click(); a.remove();
        }
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      });
    } catch {
      toast.error("Could not save image.");
    }
  };

  const remove = async () => {
    const ok = await confirm({
      title: "Delete this obituary?",
      message: "This cannot be undone. The memorial page, PDF booklet, photos and any guestbook entries will be permanently removed.",
      confirmLabel: "Delete forever",
      cancelLabel: "Keep it",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/obituaries/${id}`);
      toast.success("Obituary deleted.");
      nav("/dashboard");
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Could not delete this obituary.";
      toast.error(detail);
    }
  };

  // Persist any field updates onto the obituary so PDF/booklet stays in sync.
  const persist = async (patch) => {
    const merged = { ...doc, ...patch };
    await api.put(`/obituaries/${id}`, {
      deceased_name: merged.deceased_name,
      body: merged.body,
      portrait_b64: merged.portrait_b64,
      photos: merged.photos || [],
      cover_photo_index: merged.cover_photo_index ?? null,
      born: merged.born, born_place: merged.born_place,
      died: merged.died, died_place: merged.died_place,
      funeral_home_name: merged.funeral_home_name,
      funeral_home_address: merged.funeral_home_address,
      private_viewing: merged.private_viewing,
      wake_viewing: merged.wake_viewing,
      funeral_service_time: merged.funeral_service_time,
      interment: merged.interment, repass: merged.repass, repass_time: merged.repass_time,
      order_of_service: merged.order_of_service,
      acknowledgement: merged.acknowledgement,
      quotes: merged.quotes || [],
      theme: merged.theme || "classic",
      cover_heading: merged.cover_heading,
      badge_medallion: !!merged.badge_medallion,
      public_enabled: merged.public_enabled !== undefined ? !!merged.public_enabled : undefined,
      family_contact_email: merged.family_contact_email,
    });
    setDoc(merged);
  };

  // Set a photo as the front-cover hero. Pass null to clear and revert to the portrait_b64.
  const setCoverPhoto = async (idx) => {
    try {
      const next = (doc.cover_photo_index === idx) ? null : idx;
      await persist({ cover_photo_index: next });
      toast.success(next === null ? "Cover photo cleared." : "Cover photo updated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update cover photo.");
    }
  };

  // ─── AI ─── //
  const aiQuotes = async () => {
    setModal("quotes");
    setQuotesBusy(true);
    setQuotes([]);
    try {
      const { data } = await api.post("/ai/quotes", { deceased_name: doc.deceased_name, theme: "comfort" });
      setQuotes(data.quotes || []);
      await persist({ quotes: data.quotes || [] });
    } catch { toast.error("Generation failed"); } finally { setQuotesBusy(false); }
  };

  const aiOos = async () => {
    setOosBusy(true);
    try {
      const elements = [];
      if (oosForm.scripture) elements.push("Scripture");
      if (oosForm.eulogy) elements.push("Eulogy");
      if (oosForm.communion) elements.push("Communion");
      const notes = `Venue: ${oosForm.venue || "—"}. Officiant: ${oosForm.officiant || "—"}. Hymns: ${oosForm.hymns || "—"}. Include: ${elements.join(", ") || "standard order"}. ${oosForm.notes}`.trim();
      const { data } = await api.post("/ai/order-of-service", { deceased_name: doc.deceased_name, service_type: "traditional", notes });
      setOosResult(data.text);
      await persist({ order_of_service: data.text });
    } catch { toast.error("Generation failed"); } finally { setOosBusy(false); }
  };

  const aiAck = async () => {
    setAckBusy(true);
    try {
      const { data } = await api.post("/ai/acknowledgement", { deceased_name: doc.deceased_name, family_name: ackForm.signed_by });
      const sig = ackForm.signed_by ? `\n\n— ${ackForm.signed_by}` : "";
      const mentions = ackForm.special ? `\n\nWith special thanks to ${ackForm.special}.` : "";
      const finalText = data.text + mentions + sig;
      setAckResult(finalText);
      await persist({ acknowledgement: finalText });
    } catch { toast.error("Generation failed"); } finally { setAckBusy(false); }
  };

  // ─── Photos ─── //
  const onAddPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const readers = files.map(f => new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(String(r.result).split(",")[1]);
      r.onerror = rej;
      r.readAsDataURL(f);
    }));
    const newPhotos = await Promise.all(readers);
    const photos = [...(doc.photos || []), ...newPhotos];
    await api.put(`/obituaries/${id}`, {
      deceased_name: doc.deceased_name, body: doc.body, portrait_b64: doc.portrait_b64,
      photos, born: doc.born, died: doc.died,
    });
    toast.success(`${newPhotos.length} photo${newPhotos.length > 1 ? "s" : ""} added.`);
    await refresh();
  };

  const removePhotoAt = async (idx) => {
    const photos = (doc.photos || []).filter((_, i) => i !== idx);
    await api.put(`/obituaries/${id}`, {
      deceased_name: doc.deceased_name, body: doc.body, portrait_b64: doc.portrait_b64,
      photos, born: doc.born, died: doc.died,
    });
    await refresh();
  };

  return (
    <div style={{ maxWidth: 980, margin: "30px auto", padding: "0 24px 100px" }}>
      <Link to="/dashboard" className="muted" style={{ textDecoration: "none", fontSize: 13 }}>← Back to library</Link>

      <article className="bg-card" style={{ padding: 40, marginTop: 16 }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ fontSize: 11, letterSpacing: "0.3em", color: "var(--gold)", marginBottom: 12 }}>{(doc.cover_heading || "IN LOVING MEMORY").toUpperCase()}</div>
          {(() => {
            const idx = doc.cover_photo_index;
            const useCoverPhoto = (idx !== null && idx !== undefined && Array.isArray(doc.photos) && doc.photos[idx]);
            const heroSrc = useCoverPhoto
              ? `data:image/jpeg;base64,${doc.photos[idx]}`
              : (doc.portrait_b64 ? `data:image/png;base64,${doc.portrait_b64}` : null);
            const heroAlt = useCoverPhoto ? "cover-photo" : "portrait";
            const heroDl = useCoverPhoto
              ? () => downloadImage(doc.photos[idx], `cover-${doc.deceased_name.replace(/\s+/g, "-")}.jpg`)
              : () => downloadImage(doc.portrait_b64, `portrait-${doc.deceased_name.replace(/\s+/g, "-")}.png`);
            if (!heroSrc) return null;
            return (
              <div data-testid="cover-hero-wrap" style={{ display: "inline-block", position: "relative", marginBottom: 18 }}>
                <img src={heroSrc} alt={heroAlt} style={{ width: 180, height: 180, borderRadius: "50%", objectFit: "cover", border: "3px solid var(--gold)", display: "block" }} />
                <button
                  data-testid="portrait-download"
                  onClick={heroDl}
                  aria-label="Save cover photo"
                  title="Save cover photo"
                  style={{ position: "absolute", bottom: 6, right: 6, width: 34, height: 34, borderRadius: "50%", background: "rgba(15,15,17,0.85)", color: "var(--gold)", border: "1px solid var(--gold)", cursor: "pointer", fontSize: 14 }}
                >↓</button>
              </div>
            );
          })()}
          <h1 className="serif" style={{ fontSize: 44, color: "#fff", margin: "0 0 6px", fontWeight: 400 }}>{doc.deceased_name}</h1>
          {(doc.born || doc.died) && (
            <div className="muted" style={{ fontSize: 14, fontStyle: "italic" }}>{doc.born || "?"} — {doc.died || "?"}</div>
          )}
          <div className="gold-line" style={{ margin: "20px auto" }} />
        </div>
        <div style={{ whiteSpace: "pre-wrap", fontFamily: "Cormorant Garamond, serif", fontSize: 19, lineHeight: 1.85, color: "#e6e6e6", textAlign: "left" }}>
          {doc.body}
        </div>

        {doc.photos?.length > 0 && (
          <div style={{ marginTop: 36 }}>
            <div className="gold-line" style={{ margin: "20px auto" }} />
            <div className="serif" style={{ fontSize: 24, color: "var(--gold)", textAlign: "center", marginBottom: 8 }}>Photographs</div>
            <div className="muted" style={{ fontSize: 12, textAlign: "center", marginBottom: 16 }}>
              ★ Tap the star to make any photo the front-cover hero on the PDF & memorial page
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 10 }}>
              {doc.photos.map((p, i) => {
                const isCover = doc.cover_photo_index === i;
                return (
                  <div key={i} style={{ position: "relative" }}>
                    <img
                      src={`data:image/jpeg;base64,${p}`}
                      alt={`photo-${i}`}
                      style={{
                        width: "100%", height: 160, objectFit: "cover", borderRadius: 8,
                        border: isCover ? "3px solid var(--gold)" : "1px solid transparent",
                        boxShadow: isCover ? "0 0 0 3px rgba(212,175,55,0.18)" : "none",
                      }}
                    />
                    {isCover && (
                      <div style={{
                        position: "absolute", top: 6, left: 6,
                        background: "var(--gold)", color: "#0a0a0b",
                        fontSize: 10, fontWeight: 700, letterSpacing: "0.06em",
                        padding: "3px 7px", borderRadius: 4,
                      }} data-testid={`view-photo-cover-badge-${i}`}>COVER ★</div>
                    )}
                    <button
                      data-testid={`photo-set-cover-${i}`}
                      onClick={() => setCoverPhoto(i)}
                      aria-label={isCover ? "Unset as cover" : "Set as cover photo"}
                      title={isCover ? "Currently the cover photo — tap to clear" : "Set as cover photo"}
                      style={{ position: "absolute", top: 6, right: 38, background: isCover ? "var(--gold)" : "rgba(15,15,17,0.85)", color: isCover ? "#0a0a0b" : "var(--gold)", border: "1px solid var(--gold)", borderRadius: 999, width: 28, height: 28, cursor: "pointer", fontSize: 14, fontWeight: 700 }}
                    >★</button>
                    <button
                      data-testid={`photo-download-${i}`}
                      onClick={() => downloadImage(p, `photo-${doc.deceased_name.replace(/\s+/g, "-")}-${i + 1}.jpg`)}
                      aria-label="Save photo"
                      title="Save photo"
                      style={{ position: "absolute", bottom: 4, left: 4, background: "rgba(15,15,17,0.85)", color: "var(--gold)", border: "1px solid var(--gold)", borderRadius: 999, width: 28, height: 28, cursor: "pointer", fontSize: 13 }}
                    >↓</button>
                    <button onClick={() => removePhotoAt(i)} aria-label="Remove photo" style={{ position: "absolute", top: 6, right: 6, background: "rgba(0,0,0,0.7)", color: "#fff", border: "none", borderRadius: 999, width: 26, height: 26, cursor: "pointer" }}>×</button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <div style={{ textAlign: "center", marginTop: 30 }} className="muted">
          <em style={{ fontSize: 12 }}>— Composed with Obituary Relief —</em>
        </div>
      </article>

      {/* Floating action bar */}
      {presence.length > 0 && (
        <div data-testid="presence-bar" className="bg-card" style={{ padding: 10, marginTop: 14, fontSize: 12, color: "#aaa", textAlign: "center" }}>
          <span style={{ color: "var(--gold)" }}>●</span> {presence.map(p => p.user_name).join(", ")} {presence.length === 1 ? "is" : "are"} also viewing this obituary right now
        </div>
      )}
      <div className="bg-card" style={{ padding: 14, marginTop: 18, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
        <button data-testid="action-share" onClick={() => setModal("share")} className="btn-ghost" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          <span style={{ color: "var(--gold)" }}>✉</span> Share obituary
        </button>
        <button data-testid="action-oos" onClick={() => { setOosResult(""); setModal("oos"); }} className="btn-ghost" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          <span style={{ color: "var(--gold)" }}>≡</span> Order of Service
        </button>
        <button data-testid="action-ack" onClick={() => { setAckResult(""); setModal("ack"); }} className="btn-ghost" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          <span style={{ color: "var(--gold)" }}>♥</span> Acknowledgement
        </button>
        <button data-testid="action-quotes" onClick={aiQuotes} className="btn-ghost" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          <span style={{ color: "var(--gold)" }}>❝</span> Meaningful Quotes
        </button>
        <button data-testid="action-add-photos" onClick={() => photoInputRef.current?.click()} className="btn-ghost" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          <span style={{ color: "var(--gold)" }}>❍</span> Add Photos
        </button>
        <input ref={photoInputRef} type="file" accept="image/*" multiple onChange={onAddPhotos} style={{ display: "none" }} />
        <button data-testid="action-prayer-cards" onClick={downloadPrayerCards} className="btn-ghost" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          <span style={{ color: "var(--gold)" }}>✦</span> Prayer Cards
        </button>
        <button data-testid="action-thankyou-cards" onClick={downloadThankYouCards} className="btn-ghost" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          <span style={{ color: "var(--gold)" }}>❤</span> Thank-You Cards
        </button>
        <button data-testid="action-tribute-video" onClick={startTributeVideo} className="btn-ghost" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          <span style={{ color: "var(--gold)" }}>▶</span> Slideshow Video — Preview Free
        </button>

        <button data-testid="action-rhyming-poem" onClick={startRhymingPoem} className="btn-ghost" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          <span style={{ color: "var(--gold)" }}>📜</span> Rhyming Poem — $8.99
        </button>
        <button data-testid="action-print" onClick={orderPrintedPrograms} className="btn-ghost" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          <span style={{ color: "var(--gold)" }}>⎙</span> Order Printed Programs
        </button>
        <button data-testid="action-edit" onClick={openEdit} className="btn-ghost" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          <span style={{ color: "var(--gold)" }}>✎</span> Edit
        </button>
        <button data-testid="action-cover" onClick={openCover} className="btn-ghost" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          <span style={{ color: "var(--gold)" }}>◈</span> Customize Cover
        </button>
        <button data-testid="action-donations" onClick={openDonations} className="btn-ghost" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          <span style={{ color: "var(--gold)" }}>💝</span> {doc.stripe_connect_account_id ? "Manage donations" : "Enable donations"}
        </button>
        <button data-testid="action-delete" onClick={remove} className="btn-ghost" style={{ color: "#ff8a8a", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          <span>🗑</span> Delete
        </button>
      </div>

      {/* ─── Public Memorial Page & Sympathy Marketplace ─── */}
      <div className="bg-card" data-testid="memorial-marketplace-card" style={{ padding: 22, marginTop: 22, border: `1px solid ${doc.public_enabled ? "#3d2f12" : "#2a2a2a"}`, background: doc.public_enabled ? "linear-gradient(180deg, #14110a 0%, #0f0f10 100%)" : "#0f0f10" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 11, letterSpacing: "0.3em", color: "var(--gold)", marginBottom: 4 }}>PUBLIC MEMORIAL PAGE</div>
            <div className="serif" style={{ fontSize: 22, color: "#fff" }}>
              {doc.public_enabled ? "Live — visitors can light candles & send gifts" : "Not published yet"}
            </div>
          </div>
          <button data-testid="toggle-publish-btn" onClick={togglePublish} disabled={publishBusy} className={doc.public_enabled ? "btn-ghost" : "btn-gold"} style={{ padding: "10px 18px", fontSize: 13 }}>
            {publishBusy ? "Saving…" : (doc.public_enabled ? "Unpublish" : "Publish memorial")}
          </button>
        </div>

        {doc.public_enabled && doc.public_slug && (
          <div data-testid="memorial-published-block" style={{ background: "#0a0a0b", border: "1px solid #2a2a2a", borderRadius: 10, padding: 14, marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
            <code style={{ color: "var(--gold)", fontSize: 13, wordBreak: "break-all" }}>{window.location.origin}/m/{doc.public_slug}</code>
            <div style={{ display: "flex", gap: 8 }}>
              <button data-testid="copy-memorial-link" onClick={copyMemorialLink} className="btn-ghost" style={{ padding: "8px 14px", fontSize: 12 }}>Copy link</button>
              <Link data-testid="view-memorial-link" to={`/m/${doc.public_slug}`} target="_blank" rel="noopener" className="btn-gold" style={{ padding: "8px 14px", fontSize: 12, textDecoration: "none" }}>View memorial →</Link>
            </div>
          </div>
        )}

        <div style={{ fontSize: 12, color: "var(--gold)", letterSpacing: "0.2em", marginBottom: 10 }}>WHAT VISITORS CAN SEND</div>

        {/* Memorial Pack Bundle CTA — high-value upsell */}
        <div data-testid="bundle-cta" style={{ background: "linear-gradient(135deg, #1a140a 0%, #14110a 100%)", border: "1px solid #3d2f12", borderRadius: 12, padding: 16, marginBottom: 14, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontSize: 11, letterSpacing: "0.3em", color: "var(--gold)", marginBottom: 4 }}>💎 COMPLETE MEMORIAL PACK</div>
            <div className="serif" style={{ fontSize: 18, color: "#fff", marginBottom: 4 }}>
              Slideshow + Poem + Sympathy Gift — <b style={{ color: "var(--gold)" }}>$24.99</b> <span style={{ textDecoration: "line-through", color: "#666", fontSize: 13 }}>$28.97</span>
            </div>
            <div className="muted" style={{ fontSize: 12 }}>Save $4 — bundle 1 Slideshow Video + 1 Rhyming Poem + 1 Sympathy Gift credit. All credits applied to your next previews.</div>
          </div>
          <button
            data-testid="bundle-buy-btn"
            onClick={async () => {
              try {
                const { data } = await api.post("/bundle/checkout", { origin_url: window.location.origin, obituary_id: id });
                if (data.checkout_url) window.location.href = data.checkout_url;
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Could not start bundle checkout.");
              }
            }}
            className="btn-gold"
            style={{ padding: "12px 22px", fontSize: 13, whiteSpace: "nowrap" }}
          >Buy bundle</button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 10 }}>
          {[
            { icon: "🕯", title: "Virtual Candle", price: "Free" },
            { icon: "💌", title: "Sympathy Card", price: "$3.99" },
            { icon: "💐", title: "Flower Bouquet", price: "$5.99" },
            { icon: "▶", title: "Slideshow Video", price: "$9.99" },
            { icon: "📜", title: "Rhyming Poem", price: "$8.99" },
          ].map((p, i) => (
            <div key={i} style={{ background: "#0a0a0b", border: "1px solid #2a2a2a", borderRadius: 10, padding: 14, textAlign: "center" }}>
              <div style={{ fontSize: 24, marginBottom: 4 }}>{p.icon}</div>
              <div style={{ fontSize: 13, color: "#fff", marginBottom: 2 }}>{p.title}</div>
              <div style={{ fontSize: 11, color: "var(--gold)", fontWeight: 700 }}>{p.price}</div>
            </div>
          ))}
        </div>

        {!doc.public_enabled && (
          <div className="muted" style={{ fontSize: 12, marginTop: 14 }}>
            Publish to unlock the public memorial page where visitors can light candles, sign the guestbook, and send sympathy gifts. You can also send a gift to another family below.
          </div>
        )}

        <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #2a2a2a", display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button data-testid="send-gift-btn" onClick={() => setGiftOpen(true)} className="btn-gold" style={{ padding: "10px 18px", fontSize: 13 }}>
            🎁 Send a sympathy gift
          </button>
          <span className="muted" style={{ fontSize: 12, alignSelf: "center" }}>Send a card or flowers to another grieving family by email or text.</span>
        </div>
      </div>

      <GiftSendModal open={giftOpen} onClose={() => setGiftOpen(false)} obituaryId={id} recipientHint={{ name: doc.deceased_name }} />

      {/* ─── My Tribute Videos ─── */}
      {videos.length > 0 && (
        <div className="bg-card" data-testid="my-tribute-videos" style={{ padding: 22, marginTop: 22, border: "1px solid #2a2a2a" }}>
          <div style={{ fontSize: 11, letterSpacing: "0.3em", color: "var(--gold)", marginBottom: 4 }}>MY TRIBUTE VIDEOS</div>
          <div className="serif" style={{ fontSize: 20, color: "#fff", marginBottom: 14 }}>Preview & unlock</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {videos.map(v => {
              const statusLabel = {
                generating_preview: "🟡 Generating preview… (3–6 min)",
                preview_ready: "🟢 Preview ready — watch & decide",
                paid: "✅ Unlocked — download anytime",
                failed: "🔴 Failed",
                pending: "⏳ Pending",
              }[v.status] || v.status;
              return (
                <Link
                  key={v.id}
                  to={`/tribute-video?job_id=${v.id}`}
                  data-testid={`video-job-${v.id}`}
                  style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: 14, background: "#0a0a0b", border: "1px solid #2a2a2a", borderRadius: 10, textDecoration: "none", color: "#ddd", gap: 12, flexWrap: "wrap" }}
                >
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ fontSize: 13, color: "#fff", marginBottom: 2 }}>{v.deceased_name || "Tribute video"}</div>
                    <div style={{ fontSize: 12, color: "#bbb" }}>{statusLabel}</div>
                    {v.status === "failed" && v.error && (
                      <div style={{ fontSize: 11, color: "#f5b5b5", marginTop: 4 }}>{v.error}</div>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--gold)" }}>{v.status === "preview_ready" ? "Watch →" : v.status === "paid" ? "Download →" : "Open →"}</div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* ─── Candles + Guestbook the family received ─── */}
      {(candles.length > 0 || guestbook.length > 0) && (
        <div className="bg-card" data-testid="tributes-received" style={{ padding: 22, marginTop: 22, border: "1px solid #2a2a2a" }}>
          <div style={{ fontSize: 11, letterSpacing: "0.3em", color: "var(--gold)", marginBottom: 4 }}>TRIBUTES RECEIVED</div>
          <div className="serif" style={{ fontSize: 20, color: "#fff", marginBottom: 14 }}>
            {candles.length} candle{candles.length === 1 ? "" : "s"} • {guestbook.length} guestbook message{guestbook.length === 1 ? "" : "s"}
          </div>

          {candles.length > 0 && (
            <div style={{ marginBottom: guestbook.length > 0 ? 22 : 0 }}>
              <div style={{ fontSize: 12, color: "var(--gold)", letterSpacing: "0.18em", marginBottom: 10 }}>🕯 CANDLES LIT</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 10 }}>
                {candles.slice(0, 8).map(c => (
                  <div key={c.id} style={{ padding: 12, background: "#0a0a0b", border: "1px solid #2a2a2a", borderRadius: 8 }}>
                    <div style={{ fontSize: 13, color: "#fff", marginBottom: 4 }}>🕯 {c.visitor_name || "Anonymous friend"}</div>
                    {c.message && <div style={{ fontSize: 12, color: "#bbb", fontStyle: "italic" }}>&ldquo;{c.message}&rdquo;</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {guestbook.length > 0 && (
            <div>
              <div style={{ fontSize: 12, color: "var(--gold)", letterSpacing: "0.18em", marginBottom: 10 }}>📖 GUESTBOOK</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {guestbook.slice(0, 10).map(g => (
                  <div key={g.id} style={{ padding: 14, background: "#0a0a0b", border: "1px solid #2a2a2a", borderRadius: 10 }}>
                    <div style={{ fontSize: 13, color: "#fff", marginBottom: 4 }}>{g.visitor_name || "Anonymous"}</div>
                    <div style={{ fontSize: 13, color: "#ddd", lineHeight: 1.6 }}>{g.message}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── Modals ─── */}
      {modal === "share" && (
        <Modal onClose={() => setModal(null)} title="Share obituary" subtitle={<>In Loving Memory of <em>{doc.deceased_name}</em></>}>
          <div style={{ display: "grid", gap: 10 }}>
            <ShareRow testid="share-email-app" icon="✉" iconBg="rgba(212,175,55,0.18)" title="Email" desc="Open your mail app with the obituary pre-filled" onClick={openMailto} />
            <ShareRow testid="share-pdf" icon="▤" iconBg="rgba(180,120,40,0.22)" title="Download as PDF" desc="Beautifully formatted (with photos), ready to print or attach" onClick={downloadPdf} />
            <ShareRow testid="share-txt" icon="↓" iconBg="rgba(60,120,80,0.25)" title="Save as text file" desc="Plain text — works everywhere" onClick={downloadText} />
            <ShareRow testid="share-copy" icon="❐" iconBg="rgba(80,80,120,0.25)" title="Copy to clipboard" desc="Paste anywhere — text messages, notes, social media" onClick={copyText} />
            {doc.portrait_b64 && (
              <ShareRow testid="share-portrait" icon="◐" iconBg="rgba(212,175,55,0.18)" title="Save AI portrait" desc="Download the portrait image to your device" onClick={() => { downloadImage(doc.portrait_b64, `portrait-${doc.deceased_name.replace(/\s+/g, "-")}.png`); setModal(null); }} />
            )}
            {doc.photos?.length > 0 && (
              <ShareRow testid="share-photos-all" icon="❍" iconBg="rgba(80,140,180,0.22)" title={`Save all ${doc.photos.length} photo${doc.photos.length > 1 ? "s" : ""}`} desc="Download each photo to your device" onClick={() => {
                doc.photos.forEach((p, i) => setTimeout(() => downloadImage(p, `photo-${doc.deceased_name.replace(/\s+/g, "-")}-${i + 1}.jpg`), i * 350));
                toast.success("Saving photos…");
                setModal(null);
              }} />
            )}
            {(doc.photos?.length > 0 || doc.portrait_b64) && (
              <ShareRow testid="share-photos-email" icon="✉" iconBg="rgba(180,80,120,0.22)" title="Email photos" desc="Send all photos (and portrait) to someone via email" onClick={() => { setModal("photos-email"); }} />
            )}
            <div style={{ borderTop: "1px solid var(--border)", marginTop: 8, paddingTop: 14 }}>
              <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>Or send the obituary directly by email (with portrait):</div>
              <form onSubmit={sendEmail} style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <input data-testid="share-email-input" type="email" required value={shareEmail} onChange={(e) => setShareEmail(e.target.value)} placeholder="recipient@email.com" className="input-dark" style={{ flex: "1 1 200px" }} />
                <button data-testid="share-email-send" type="submit" disabled={shareBusy} className="btn-gold">{shareBusy ? "Sending…" : "Send"}</button>
              </form>
            </div>
          </div>
        </Modal>
      )}

      {modal === "edit" && (
        <Modal onClose={() => setModal(null)} title="Edit obituary" subtitle="Update the name or rewrite any part of the obituary text.">
          <form onSubmit={saveEdit} style={{ display: "grid", gap: 12 }}>
            <div>
              <label className="label-dark">Full name</label>
              <input data-testid="edit-name" required value={editName} onChange={(e) => setEditName(e.target.value)} className="input-dark" />
            </div>
            <div>
              <label className="label-dark">Obituary text</label>
              <textarea data-testid="edit-body" required rows={16} value={editBody} onChange={(e) => setEditBody(e.target.value)} className="input-dark" style={{ resize: "vertical", fontFamily: "Cormorant Garamond, serif", fontSize: 16, lineHeight: 1.75 }} />
            </div>
            <button data-testid="edit-save" type="submit" disabled={editBusy} className="btn-gold">{editBusy ? "Saving…" : "Save changes"}</button>
          </form>
        </Modal>
      )}

      {modal === "slideshow" && (
        <Modal onClose={() => setModal(null)} title="Create slideshow tribute" subtitle="A heartfelt Ken Burns slideshow built from your uploaded photos. Up to 5 minutes long, with optional narration. Free preview — pay $9.99 only if you love it.">
          <form onSubmit={submitSlideshow} style={{ display: "grid", gap: 14 }}>
            <div>
              <label className="label-dark">Background music</label>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 8, marginBottom: 6 }}>
                {(slideForm.tracks || []).map(t => (
                  <button type="button" key={t.id} data-testid={`slideshow-track-${t.id}`} onClick={() => setSlideForm(f => ({ ...f, music_track: t.id }))} style={{
                    padding: 12, borderRadius: 10, cursor: "pointer", fontSize: 13, fontFamily: "inherit", textAlign: "left",
                    background: slideForm.music_track === t.id ? "rgba(212,175,55,0.18)" : "transparent",
                    color: "#fff",
                    border: "1px solid " + (slideForm.music_track === t.id ? "var(--gold)" : "#2a2a2a"),
                  }}>
                    <div style={{ fontWeight: 600 }}>♪ {t.name}</div>
                  </button>
                ))}
              </div>
              {(slideForm.tracks || []).length === 0 && (
                <div className="muted" style={{ fontSize: 12, padding: 12, textAlign: "center", border: "1px dashed #3a3a3a", borderRadius: 10 }}>
                  Loading music library…
                </div>
              )}
            </div>

            {/* ── Voiceover narration (NEW) ────────────────────────────── */}
            <div style={{ padding: 14, border: "1px solid #2a2a2a", borderRadius: 10, background: "#0a0a0b" }}>
              <label className="label-dark" style={{ marginBottom: 8 }}>Voiceover narration (optional)</label>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
                {[
                  { id: "none",   label: "🎵 Music only" },
                  { id: "tts",    label: "🎙 AI narration" },
                  { id: "upload", label: "📤 Upload my recording" },
                ].map(o => (
                  <button
                    key={o.id}
                    type="button"
                    data-testid={`voiceover-mode-${o.id}`}
                    onClick={() => setSlideForm(f => ({ ...f, voiceover_mode: o.id }))}
                    style={{
                      padding: "8px 14px", borderRadius: 999, cursor: "pointer", fontSize: 12, fontFamily: "inherit",
                      background: slideForm.voiceover_mode === o.id ? "var(--gold)" : "transparent",
                      color: slideForm.voiceover_mode === o.id ? "#0a0a0b" : "#ddd",
                      border: "1px solid " + (slideForm.voiceover_mode === o.id ? "var(--gold)" : "#2a2a2a"),
                    }}
                  >{o.label}</button>
                ))}
              </div>

              {slideForm.voiceover_mode === "tts" && (
                <div style={{ display: "grid", gap: 10 }}>
                  <div>
                    <label className="label-dark" style={{ fontSize: 11 }}>Voice</label>
                    <select
                      data-testid="voiceover-voice"
                      value={slideForm.voiceover_voice}
                      onChange={e => setSlideForm(f => ({ ...f, voiceover_voice: e.target.value }))}
                      className="input-dark"
                      style={{ padding: 10 }}
                    >
                      {(slideForm.voices || []).map(v => (
                        <option key={v.id} value={v.id}>{v.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="label-dark" style={{ fontSize: 11 }}>Script — paste the obituary, write a tribute, or any short narration</label>
                    <textarea
                      data-testid="voiceover-text"
                      rows={6}
                      maxLength={4000}
                      value={slideForm.voiceover_text}
                      onChange={e => setSlideForm(f => ({ ...f, voiceover_text: e.target.value }))}
                      className="input-dark"
                      placeholder={`Margaret Thompson was born on March 14, 1942 in Memphis, Tennessee.\n\nShe was a nurse for 38 years, a devoted mother to Sarah and Michael, and a beloved grandmother to five.\n\nShe passed peacefully on December 2, 2025, surrounded by family.`}
                      style={{ resize: "vertical", fontFamily: "Cormorant Garamond, serif", fontSize: 14, lineHeight: 1.7 }}
                    />
                    <div className="muted" style={{ fontSize: 10, marginTop: 4 }}>
                      {(slideForm.voiceover_text || "").length} / 4000 characters • Music will gently duck under the voice
                    </div>
                  </div>
                  <button
                    type="button"
                    data-testid="voiceover-paste-body"
                    onClick={() => setSlideForm(f => ({ ...f, voiceover_text: (doc.body || "").slice(0, 4000) }))}
                    className="btn-ghost"
                    style={{ padding: "8px 14px", fontSize: 12, justifySelf: "start" }}
                  >↓ Paste obituary text</button>
                </div>
              )}

              {slideForm.voiceover_mode === "upload" && (
                <div>
                  <label className="label-dark" style={{ fontSize: 11 }}>Upload an MP3 recording (family member voice, etc.)</label>
                  <input
                    data-testid="voiceover-upload"
                    type="file"
                    accept="audio/mpeg,audio/mp3,audio/wav,audio/x-m4a,audio/mp4,audio/aac"
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      if (f.size > 25 * 1024 * 1024) { toast.error("Audio file too large — keep under 25MB."); return; }
                      const reader = new FileReader();
                      reader.onload = () => setSlideForm(s => ({ ...s, voiceover_b64: String(reader.result).split(",")[1], voiceover_filename: f.name }));
                      reader.readAsDataURL(f);
                    }}
                    className="input-dark"
                  />
                  {slideForm.voiceover_filename && (
                    <div className="muted" style={{ fontSize: 11, marginTop: 6 }}>✓ Loaded: {slideForm.voiceover_filename}</div>
                  )}
                </div>
              )}

              {slideForm.voiceover_mode === "none" && (
                <div className="muted" style={{ fontSize: 11, fontStyle: "italic" }}>
                  No narration. Music will play at full volume across the slideshow.
                </div>
              )}
            </div>

            <div>
              <label className="label-dark">Notify by email (optional)</label>
              <input data-testid="slideshow-recipient-email" type="email" className="input-dark" value={slideForm.recipient_email} onChange={e => setSlideForm({ ...slideForm, recipient_email: e.target.value })} placeholder="you@example.com" />
            </div>
            <button data-testid="slideshow-submit" type="submit" disabled={slideBusy} className="btn-gold" style={{ marginTop: 4 }}>
              {slideBusy ? "Starting render…" : "Build slideshow preview (free)"}
            </button>
            <div className="muted" style={{ fontSize: 11, marginTop: -4 }}>
              We&apos;ll use the photos already uploaded to this obituary. Render takes ~30–90 seconds. No payment until you unlock the watermark-free version.
            </div>
          </form>
        </Modal>
      )}



      {modal === "poem-options" && (
        <Modal onClose={() => setModal(null)} title="Customized rhyming poem" subtitle="A bespoke ABAB rhyming tribute, composed in 10–20 seconds. $8.99 — paid first, with watermark-free PDF and 5 free re-rolls included.">
          <div style={{ display: "grid", gap: 14 }}>
            <div>
              <label className="label-dark">Tone</label>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8 }}>
                {[
                  { id: "loving", label: "Loving", desc: "Tender, warm" },
                  { id: "faith", label: "Faith-filled", desc: "Gentle, heavenly" },
                  { id: "celebratory", label: "Celebratory", desc: "Uplifting" },
                  { id: "humorous", label: "Humorous", desc: "Light, fond smile" },
                ].map(t => (
                  <button
                    key={t.id}
                    type="button"
                    data-testid={`poem-tone-${t.id}`}
                    onClick={() => setPoemForm(f => ({ ...f, tone: t.id }))}
                    style={{
                      padding: 14, borderRadius: 10, cursor: "pointer", fontSize: 13, fontFamily: "inherit", textAlign: "left",
                      background: poemForm.tone === t.id ? "rgba(212,175,55,0.18)" : "transparent",
                      color: "#fff",
                      border: "1px solid " + (poemForm.tone === t.id ? "var(--gold)" : "#2a2a2a"),
                    }}
                  >
                    <div style={{ fontWeight: 600, marginBottom: 2 }}>{t.label}</div>
                    <div className="muted" style={{ fontSize: 11 }}>{t.desc}</div>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="label-dark">Stories or character details (optional)</label>
              <textarea
                data-testid="poem-notes"
                rows={4}
                value={poemForm.notes}
                onChange={e => setPoemForm(f => ({ ...f, notes: e.target.value }))}
                className="input-dark"
                placeholder="e.g., She loved Sunday roasts, gardenias, and her grandkids' laughter. Worked as a nurse for 38 years."
                style={{ resize: "vertical" }}
              />
            </div>
            <button data-testid="poem-submit" onClick={submitPoemCheckout} disabled={poemBusy} className="btn-gold" style={{ padding: 14 }}>
              {poemBusy ? "Opening checkout…" : "Continue to checkout — $8.99 →"}
            </button>
            <div className="muted" style={{ fontSize: 11, marginTop: -4, textAlign: "center" }}>
              Have a bundle credit? It will be applied automatically.
            </div>
          </div>
        </Modal>
      )}

      {modal === "print" && (
        <Modal onClose={() => setModal(null)} title="Order printed programs" subtitle="High-quality folded memorial booklets, mailed to you. 25–500 copies.">
          <form onSubmit={submitPrintOrder} style={{ display: "grid", gap: 14 }}>
            <div>
              <label className="label-dark">Quantity</label>
              <input data-testid="print-qty" type="number" min={25} max={500} required value={printForm.quantity} onChange={e => setPrintForm(f => ({ ...f, quantity: e.target.value }))} className="input-dark" />
              <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>Minimum 25, maximum 500.</div>
            </div>
            <div>
              <label className="label-dark">Shipping name</label>
              <input data-testid="print-name" required value={printForm.name} onChange={e => setPrintForm(f => ({ ...f, name: e.target.value }))} className="input-dark" />
            </div>
            <div>
              <label className="label-dark">Address line 1</label>
              <input data-testid="print-addr1" required value={printForm.addr1} onChange={e => setPrintForm(f => ({ ...f, addr1: e.target.value }))} className="input-dark" />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 100px 110px", gap: 10 }}>
              <div>
                <label className="label-dark">City</label>
                <input data-testid="print-city" required value={printForm.city} onChange={e => setPrintForm(f => ({ ...f, city: e.target.value }))} className="input-dark" />
              </div>
              <div>
                <label className="label-dark">State</label>
                <input data-testid="print-state" required maxLength={2} value={printForm.state} onChange={e => setPrintForm(f => ({ ...f, state: e.target.value.toUpperCase() }))} className="input-dark" placeholder="TX" />
              </div>
              <div>
                <label className="label-dark">ZIP</label>
                <input data-testid="print-zip" required value={printForm.zip} onChange={e => setPrintForm(f => ({ ...f, zip: e.target.value }))} className="input-dark" />
              </div>
            </div>
            <button data-testid="print-submit" type="submit" disabled={printBusy} className="btn-gold" style={{ padding: 14 }}>
              {printBusy ? "Submitting…" : "Place order"}
            </button>
          </form>
        </Modal>
      )}

      {modal === "donations" && (
        <Modal onClose={() => setModal(null)} title={donAccount?.enabled ? "Manage memorial donations" : "Enable memorial donations"} subtitle={donAccount?.enabled ? "Update the fundraising goal or share the public memorial link to start collecting donations." : "Stripe Connect routes 90% of donations straight to the family's bank account (10% platform fee). Setup takes 3–5 minutes."}>
          {!donAccount?.enabled && (
            <form onSubmit={startConnectOnboarding} style={{ display: "grid", gap: 14 }}>
              <div>
                <label className="label-dark">Memorial fund name</label>
                <input data-testid="donations-fund-name" className="input-dark" value={donForm.fund_name} onChange={e => setDonForm({ ...donForm, fund_name: e.target.value })} placeholder={`In memory of ${doc.deceased_name}`} />
                <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>Visitors will see this as the heading above the donate button.</div>
              </div>
              <div>
                <label className="label-dark">Email for the family Stripe account</label>
                <input data-testid="donations-email" type="email" required className="input-dark" value={donForm.email} onChange={e => setDonForm({ ...donForm, email: e.target.value })} placeholder="family@example.com" />
                <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>This person will receive Stripe&apos;s onboarding link &amp; eventually the deposits. Use the family rep&apos;s email.</div>
              </div>
              <div style={{ padding: 14, background: "#0e1a10", border: "1px solid #2a4a2a", borderRadius: 10, fontSize: 12, color: "#bcc" }}>
                <b style={{ color: "var(--gold)" }}>What happens next:</b>
                <ol style={{ margin: "8px 0 0 16px", paddingLeft: 0, lineHeight: 1.7 }}>
                  <li>You&apos;ll be redirected to Stripe to set up the family&apos;s account (bank info, ID).</li>
                  <li>After Stripe approves (usually instant), donations start flowing.</li>
                  <li>90% goes to the family bank account, 10% to platform fees.</li>
                  <li>Set a fundraising goal here later (e.g. $5,000 for funeral costs).</li>
                </ol>
              </div>
              <button data-testid="donations-onboard-submit" type="submit" disabled={donBusy} className="btn-gold" style={{ marginTop: 4, padding: 12 }}>
                {donBusy ? "Opening Stripe…" : "Continue to Stripe onboarding →"}
              </button>
            </form>
          )}
          {donAccount?.enabled && (
            <form onSubmit={saveDonationGoal} style={{ display: "grid", gap: 14 }}>
              <div style={{
                padding: 14, borderRadius: 10, fontSize: 13,
                background: donAccount.onboarding_complete ? "#0e1a10" : "#1a1408",
                border: "1px solid " + (donAccount.onboarding_complete ? "#2a4a2a" : "#5a4a1a"),
                color: "#ddd",
              }}>
                {donAccount.onboarding_complete
                  ? <><b style={{ color: "var(--gold)" }}>✓ Stripe ready</b> — donations will deposit directly to the family bank account.</>
                  : <><b style={{ color: "var(--gold)" }}>⏳ Stripe pending</b> — the family rep needs to finish Stripe&apos;s onboarding. Resend the link by re-running setup.</>
                }
              </div>
              <div>
                <label className="label-dark">Fundraising goal (USD) — 0 hides the bar</label>
                <input data-testid="donations-goal" type="number" min="0" step="100" className="input-dark" value={donForm.goal_usd} onChange={e => setDonForm({ ...donForm, goal_usd: e.target.value })} placeholder="5000" />
              </div>
              <div>
                <label className="label-dark">Cause (shown to visitors)</label>
                <textarea data-testid="donations-cause" rows={3} className="input-dark" value={donForm.cause} onChange={e => setDonForm({ ...donForm, cause: e.target.value })} placeholder="Help cover funeral expenses and a contribution to a charitable cause." />
              </div>
              <button data-testid="donations-save-goal" type="submit" disabled={donBusy} className="btn-gold" style={{ marginTop: 4, padding: 12 }}>
                {donBusy ? "Saving…" : "Save goal & cause"}
              </button>
            </form>
          )}
        </Modal>
      )}

      {modal === "cover" && (
        <Modal onClose={() => setModal(null)} title="Customize PDF cover" subtitle="Pick the theme & heading. These settings apply to the printable booklet PDF.">
          <form onSubmit={saveCover} style={{ display: "grid", gap: 14 }}>
            <div>
              <label className="label-dark">Cover heading</label>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
                {["In Loving Memory", "Celebrating the Life of", "Forever in Our Hearts", "A Beautiful Life Remembered", "Until We Meet Again"].map(h => (
                  <button type="button" key={h} data-testid={`cover-heading-${h.replace(/\s+/g, '-').toLowerCase()}`} onClick={() => setCoverForm(f => ({ ...f, cover_heading: h }))} style={{
                    padding: "6px 12px", borderRadius: 999, cursor: "pointer", fontSize: 12, fontFamily: "inherit",
                    background: coverForm.cover_heading === h ? "var(--gold)" : "transparent",
                    color: coverForm.cover_heading === h ? "#0a0a0b" : "#ddd",
                    border: "1px solid " + (coverForm.cover_heading === h ? "var(--gold)" : "#2a2a2a"),
                  }}>{h}</button>
                ))}
              </div>
              <input data-testid="cover-heading-input" className="input-dark" value={coverForm.cover_heading} onChange={(e) => setCoverForm({ ...coverForm, cover_heading: e.target.value })} placeholder="Or type your own heading…" />
            </div>
            <div>
              <label className="label-dark">Theme</label>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 8 }}>
                {[
                  { k: "classic", label: "Classic", swatch: "#8a6a17" },
                  { k: "heavenly", label: "Heavenly", swatch: "#3a7bd5" },
                  { k: "faith", label: "Faith", swatch: "#6b3a8a" },
                  { k: "celebration", label: "Celebration", swatch: "#c45a2e" },
                  { k: "military", label: "Military", swatch: "#1e3a5c" },
                  { k: "military_navy", label: "Navy", swatch: "#0a2a4a" },
                  { k: "military_army", label: "Army", swatch: "#3a4a1a" },
                  { k: "military_marines", label: "Marines", swatch: "#5a1a1a" },
                  { k: "garden", label: "Garden", swatch: "#3a7a4a" },
                  { k: "sunset", label: "Sunset", swatch: "#c45a3a" },
                  { k: "ocean", label: "Ocean", swatch: "#1e6a8a" },
                  { k: "scripture", label: "Scripture", swatch: "#5a4a2a" },
                  { k: "rose", label: "Rose", swatch: "#a14a6a" },
                ].map(t => (
                  <button type="button" key={t.k} data-testid={`cover-theme-${t.k}`} onClick={() => setCoverForm(f => ({ ...f, theme: t.k }))} style={{
                    padding: 10, borderRadius: 10, cursor: "pointer", fontSize: 12, fontFamily: "inherit", textAlign: "center",
                    background: coverForm.theme === t.k ? "rgba(212,175,55,0.18)" : "transparent",
                    color: "#fff",
                    border: "1px solid " + (coverForm.theme === t.k ? "var(--gold)" : "#2a2a2a"),
                  }}>
                    <div style={{ width: 24, height: 24, borderRadius: 6, background: t.swatch, margin: "0 auto 6px" }} />
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontSize: 13, color: "#ddd" }}>
              <input data-testid="cover-badge-medallion" type="checkbox" checked={coverForm.badge_medallion} onChange={(e) => setCoverForm({ ...coverForm, badge_medallion: e.target.checked })} />
              Add ornate medallion border around the portrait
            </label>
            <button data-testid="cover-save" type="submit" disabled={coverBusy} className="btn-gold" style={{ marginTop: 4 }}>{coverBusy ? "Saving…" : "Save cover settings"}</button>
            <div className="muted" style={{ fontSize: 11, marginTop: -4 }}>Tip: download the PDF again to see the new cover. Existing PDFs aren&apos;t auto-updated.</div>
          </form>
        </Modal>
      )}

      {modal === "photos-email" && (
        <Modal onClose={() => setModal(null)} title="Email photos" subtitle={`Send ${(doc.photos?.length || 0) + (doc.portrait_b64 ? 1 : 0)} image${((doc.photos?.length || 0) + (doc.portrait_b64 ? 1 : 0)) === 1 ? "" : "s"} from memory of ${doc.deceased_name}.`}>
          <form onSubmit={sendPhotosEmail} style={{ display: "grid", gap: 12 }}>
            <input data-testid="photos-email-input" type="email" required value={photosEmail} onChange={(e) => setPhotosEmail(e.target.value)} placeholder="recipient@email.com" className="input-dark" />
            <textarea data-testid="photos-email-note" rows={3} value={photosNote} onChange={(e) => setPhotosNote(e.target.value)} placeholder="Optional message (e.g., A few photos to remember her by — with love.)" className="input-dark" style={{ resize: "vertical" }} />
            <button data-testid="photos-email-send" type="submit" disabled={photosBusy} className="btn-gold" style={{ marginTop: 4 }}>{photosBusy ? "Sending…" : "Send photos"}</button>
          </form>
        </Modal>
      )}

      {modal === "oos" && (
        <Modal onClose={() => setModal(null)} title="Order of Service" subtitle="Share details, AI will compose a dignified program.">
          {!oosResult ? (
            <div style={{ display: "grid", gap: 12 }}>
              <input data-testid="oos-venue" className="input-dark" value={oosForm.venue} onChange={(e) => setOosForm({ ...oosForm, venue: e.target.value })} placeholder="Venue (e.g. St. Mary Chapel)" />
              <input data-testid="oos-officiant" className="input-dark" value={oosForm.officiant} onChange={(e) => setOosForm({ ...oosForm, officiant: e.target.value })} placeholder="Officiant (e.g. Rev. James Carter)" />
              <input data-testid="oos-hymns" className="input-dark" value={oosForm.hymns} onChange={(e) => setOosForm({ ...oosForm, hymns: e.target.value })} placeholder="Hymns / musical selections" />
              <textarea data-testid="oos-notes" className="input-dark" rows={3} value={oosForm.notes} onChange={(e) => setOosForm({ ...oosForm, notes: e.target.value })} placeholder="Other notes (optional)" style={{ resize: "vertical" }} />
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {[
                  { k: "scripture", l: "Scripture" },
                  { k: "eulogy", l: "Eulogy" },
                  { k: "communion", l: "Communion" },
                ].map((c) => (
                  <button key={c.k} data-testid={`oos-chip-${c.k}`} onClick={() => setOosForm({ ...oosForm, [c.k]: !oosForm[c.k] })} style={{
                    padding: "8px 16px", borderRadius: 999, cursor: "pointer", fontFamily: "inherit",
                    background: oosForm[c.k] ? "rgba(212,175,55,0.18)" : "transparent",
                    color: oosForm[c.k] ? "var(--gold)" : "var(--silver)",
                    border: "1px solid " + (oosForm[c.k] ? "var(--gold)" : "var(--border)"),
                  }}>{oosForm[c.k] ? "✓" : "○"} {c.l}</button>
                ))}
              </div>
              <button data-testid="oos-generate" onClick={aiOos} disabled={oosBusy} className="btn-gold" style={{ marginTop: 8 }}>{oosBusy ? "Composing…" : "Save"}</button>
            </div>
          ) : (
            <div>
              <pre style={{ whiteSpace: "pre-wrap", color: "#ddd", fontFamily: "Cormorant Garamond, serif", fontSize: 16, lineHeight: 1.85, background: "var(--bg-3)", padding: 18, borderRadius: 10, maxHeight: "60vh", overflow: "auto" }}>{oosResult}</pre>
              <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
                <button onClick={() => navigator.clipboard.writeText(oosResult).then(() => toast.success("Copied."))} className="btn-ghost">Copy</button>
                <button onClick={() => { setOosResult(""); }} className="btn-ghost">Regenerate</button>
              </div>
            </div>
          )}
        </Modal>
      )}

      {modal === "ack" && (
        <Modal onClose={() => setModal(null)} title="Word of Acknowledgement" subtitle="A short, gracious note of thanks from the family.">
          {!ackResult ? (
            <div style={{ display: "grid", gap: 12 }}>
              <input data-testid="ack-signed" className="input-dark" value={ackForm.signed_by} onChange={(e) => setAckForm({ ...ackForm, signed_by: e.target.value })} placeholder="Signed by (e.g. The Whitfield Family)" />
              <input data-testid="ack-special" className="input-dark" value={ackForm.special} onChange={(e) => setAckForm({ ...ackForm, special: e.target.value })} placeholder="Special mentions (optional)" />
              <button data-testid="ack-generate" onClick={aiAck} disabled={ackBusy} className="btn-gold" style={{ marginTop: 8 }}>{ackBusy ? "Writing…" : "Create Acknowledgement"}</button>
            </div>
          ) : (
            <div>
              <pre style={{ whiteSpace: "pre-wrap", color: "#ddd", fontFamily: "Cormorant Garamond, serif", fontSize: 17, lineHeight: 1.85, background: "var(--bg-3)", padding: 18, borderRadius: 10, maxHeight: "60vh", overflow: "auto" }}>{ackResult}</pre>
              <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
                <button onClick={() => navigator.clipboard.writeText(ackResult).then(() => toast.success("Copied."))} className="btn-ghost">Copy</button>
                <button onClick={() => { setAckResult(""); }} className="btn-ghost">Regenerate</button>
              </div>
            </div>
          )}
        </Modal>
      )}

      {modal === "quotes" && (
        <Modal onClose={() => setModal(null)} title="Meaningful Quotes" subtitle={null}>
          {quotesBusy ? (
            <div style={{ textAlign: "center", padding: 30 }}>
              <div className="shimmer" style={{ width: 38, height: 38, borderRadius: "50%", margin: "0 auto 16px" }} />
              <div className="muted" style={{ fontSize: 13 }}>Drawing quiet inspiration…</div>
            </div>
          ) : (
            <div style={{ display: "grid", gap: 10 }}>
              {quotes.map((q, i) => (
                <div key={i} className="bg-card" style={{ padding: 16, fontStyle: "italic", color: "#ddd", fontFamily: "Cormorant Garamond, serif", fontSize: 17, lineHeight: 1.6 }}>
                  &ldquo;{q}&rdquo;
                </div>
              ))}
              {quotes.length === 0 && <div className="muted" style={{ textAlign: "center" }}>No quotes generated. Try again.</div>}
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

function ShareRow({ testid, icon, iconBg, title, desc, onClick }) {
  return (
    <button data-testid={testid} onClick={onClick} style={{
      display: "flex", alignItems: "center", gap: 16, padding: 14,
      background: "var(--bg-3)", border: "1px solid var(--border)", borderRadius: 12,
      cursor: "pointer", textAlign: "left", color: "inherit", fontFamily: "inherit",
    }}>
      <div style={{ width: 44, height: 44, borderRadius: "50%", background: iconBg, color: "var(--gold)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>{icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="serif" style={{ fontSize: 18, color: "#fff" }}>{title}</div>
        <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{desc}</div>
      </div>
      <div className="muted">›</div>
    </button>
  );
}

function Modal({ title, subtitle, onClose, children }) {
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)", zIndex: 150, display: "flex", alignItems: "flex-end", justifyContent: "center", padding: 16, animation: "fadeIn 0.2s ease" }}>
      <div onClick={(e) => e.stopPropagation()} className="bg-card" style={{ padding: 28, maxWidth: 600, width: "100%", marginBottom: 24, maxHeight: "90vh", overflow: "auto" }}>
        <div style={{ textAlign: "center", marginBottom: subtitle ? 6 : 16 }}>
          <h2 className="serif" style={{ fontSize: 28, color: "#fff", fontWeight: 400, margin: 0 }}>{title}</h2>
          <div className="gold-line" style={{ margin: "12px auto" }} />
          {subtitle && <p className="muted" style={{ fontSize: 13, marginBottom: 14, marginTop: 8 }}>{subtitle}</p>}
        </div>
        {children}
        <div style={{ display: "flex", justifyContent: "center", marginTop: 18 }}>
          <button data-testid="modal-close" onClick={onClose} className="link-gold" style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, fontFamily: "inherit" }}>Close</button>
        </div>
      </div>
    </div>
  );
}
